import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import GenericInput from "src/components/input-components/GenericTextInput";
import GenericTextAreaInput from "src/components/input-components/GenericTextAreaInput";
import ImagePicker from "src/components/input-components/GenericImagePicker";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  ICompanyDetailsWidget,
  ICompanyItem,
  IPageFilter,
  ICompanyCounter,
} from "src/PageWidgetSection";

// Props for CompanyDetailsItem component
interface CompanyDetailsItemProps {
  item: ICompanyDetailsWidget;
  error: any;
  onChange: (updatedItem: ICompanyDetailsWidget, field: string) => void;
  onblur: (field: string, index?: number) => void;
}

const CompanyDetailsItem: React.FC<CompanyDetailsItemProps> = ({
  item,
  error,
  onChange,
  onblur,
}) => {
  const companyItems: ICompanyItem[] = Array.isArray(item.Items) ? item.Items : [];
  const filters: IPageFilter[] = Array.isArray(item.Filters) ? item.Filters : [];

  // Local state for image picker management.
  const [imagePickerOpen, setImagePickerOpen] = useState<boolean>(false);
  const [currentImageTarget, setCurrentImageTarget] = useState<{
    itemIndex: number;
    field: "ImageUrl" | "Images";
    subIndex?: number;
  } | null>(null);

  // Helper: extract value from an event or use it as string.
  const getValue = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ): string => (typeof e === "string" ? e : e.target.value);

  // Updates a company item in the Items array.
  const handleCompanyItemChange = (
    index: number,
    field: keyof ICompanyItem,
    value: string
  ) => {
    const updatedItems = [...companyItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  // ----- IMAGE PICKER HANDLERS -----
  const openImagePicker = (
    itemIndex: number,
    field: "ImageUrl" | "Images",
    subIndex?: number
  ) => {
    setCurrentImageTarget({ itemIndex, field, subIndex });
    setImagePickerOpen(true);
  };

  const handleImageSelect = (selected: string | string[]) => {
    if (currentImageTarget) {
      const newImage = Array.isArray(selected) ? selected[0] : selected;
      const updatedItems = [...companyItems];
      if (currentImageTarget.field === "ImageUrl") {
        updatedItems[currentImageTarget.itemIndex] = {
          ...updatedItems[currentImageTarget.itemIndex],
          ImageUrl: newImage,
        };
      } else if (
        currentImageTarget.field === "Images" &&
        currentImageTarget.subIndex !== undefined
      ) {
        const compItem = updatedItems[currentImageTarget.itemIndex];
        const images = compItem.Images || [];
        const updatedImages = [...images];
        updatedImages[currentImageTarget.subIndex] = newImage;
        updatedItems[currentImageTarget.itemIndex] = {
          ...compItem,
          Images: updatedImages,
        };
      }
      onChange({ ...item, Items: updatedItems }, "Items");
    }
    setImagePickerOpen(false);
    setCurrentImageTarget(null);
  };

  // ----- COUNTERS HANDLERS (inside Properties) -----
  const handleCounterChange = (
    itemIndex: number,
    counterIndex: number,
    field: keyof ICompanyCounter,
    value: string
  ) => {
    const updatedItems = [...companyItems];
    const compItem = updatedItems[itemIndex];
    const counters = compItem.Properties?.Counters || [];
    const updatedCounters = [...counters];
    updatedCounters[counterIndex] = { ...updatedCounters[counterIndex], [field]: value };
    updatedItems[itemIndex] = {
      ...compItem,
      Properties: { ...compItem.Properties, Counters: updatedCounters },
    };
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  const handleAddCounter = (itemIndex: number) => {
    const updatedItems = [...companyItems];
    const compItem = updatedItems[itemIndex];
    const counters = compItem.Properties?.Counters || [];
    const updatedCounters = [...counters, { Value: "", Label: "" }];
    updatedItems[itemIndex] = {
      ...compItem,
      Properties: { ...compItem.Properties, Counters: updatedCounters },
    };
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  const handleRemoveCounter = (itemIndex: number, counterIndex: number) => {
    const updatedItems = [...companyItems];
    const compItem = updatedItems[itemIndex];
    const counters = compItem.Properties?.Counters || [];
    const updatedCounters = counters.filter((_, i) => i !== counterIndex);
    updatedItems[itemIndex] = {
      ...compItem,
      Properties: { ...compItem.Properties, Counters: updatedCounters },
    };
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  // ----- HISTORY HANDLERS (for sections with nested Items) -----
  const handleHistoryChange = (
    sectionIndex: number,
    historyIndex: number,
    field: "Date" | "Title" | "Description",
    value: string
  ) => {
    const updatedItems = [...companyItems];
    const compItem = updatedItems[sectionIndex];
    const historyItems = compItem.Items || [];
    const updatedHistory = [...historyItems];
    updatedHistory[historyIndex] = { ...updatedHistory[historyIndex], [field]: value };
    updatedItems[sectionIndex] = { ...compItem, Items: updatedHistory };
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  // ----- TABS HANDLERS (for mission section) -----
  const handleTabChange = (
    sectionIndex: number,
    tabIndex: number,
    field: "Title" | "Description",
    value: string
  ) => {
    const updatedItems = [...companyItems];
    const compItem = updatedItems[sectionIndex];
    const tabs = compItem.Tabs || [];
    const updatedTabs = [...tabs];
    updatedTabs[tabIndex] = { ...updatedTabs[tabIndex], [field]: value };
    updatedItems[sectionIndex] = { ...compItem, Tabs: updatedTabs };
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  // ----- FILTERS HANDLERS -----
  const handleFilterChange = (
    index: number,
    field: keyof IPageFilter,
    value: string
  ) => {
    const updatedFilters = [...filters];
    updatedFilters[index] = { ...updatedFilters[index], [field]: value };
    onChange({ ...item, Filters: updatedFilters }, "Filters");
  };

  const handleAddFilter = () => {
    const newFilter: IPageFilter = { Link: "", Title: "" };
    const updatedFilters = [...filters, newFilter];
    onChange({ ...item, Filters: updatedFilters }, "Filters");
  };

  const handleRemoveFilter = (index: number) => {
    const updatedFilters = filters.filter((_, i) => i !== index);
    onChange({ ...item, Filters: updatedFilters }, "Filters");
  };

  return (
    <Card elevation={2} sx={{ p: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Company Details Widget
        </Typography>

        {/* Render each Company Item (Section) */}
        {companyItems.map((comp, idx) => (
          <Box
            key={idx}
            sx={{ border: "1px solid #ccc", p: 2, mb: 2, borderRadius: "4px" }}
          >
            <Typography variant="subtitle1" gutterBottom>
              Section {idx + 1}: {comp.Header || comp.Title || ""}
            </Typography>

            {comp.Header !== undefined && (
              <GenericInput
                type="text"
                name={`Header-${idx}`}
                title="Header"
                value={comp.Header || ""}
                error={error?.Items?.[idx]?.Header || ""}
                onChange={(e) => handleCompanyItemChange(idx, "Header", getValue(e))}
                onBlur={() => onblur("Header", idx)}
                disabled={false}
              />
            )}

            {comp.Title !== undefined && (
              <GenericInput
                type="text"
                name={`Title-${idx}`}
                title="Title"
                value={comp.Title || ""}
                error={error?.Items?.[idx]?.Title || ""}
                onChange={(e) => handleCompanyItemChange(idx, "Title", getValue(e))}
                onBlur={() => onblur("Title", idx)}
                disabled={false}
              />
            )}

            {comp.ImageUrl !== undefined && (
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  height: 150,
                  width: 250,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f9f9f9",
                  cursor: "pointer",
                  mb: 1,
                  overflow: "hidden",
                }}
                onClick={() => openImagePicker(idx, "ImageUrl")}
              >
                {comp.ImageUrl ? (
                  <img
                    src={comp.ImageUrl}
                    alt={`Section ${idx + 1}`}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Select Image
                  </Typography>
                )}
              </Box>
            )}

            {comp.Description !== undefined && (
              <GenericTextAreaInput
                name={`Description-${idx}`}
                title="Description"
                value={comp.Description || ""}
                error={error?.Items?.[idx]?.Description || ""}
                onChange={(e) => handleCompanyItemChange(idx, "Description", getValue(e))}
                onBlur={() => onblur("Description", idx)}
                disabled={false}
              />
            )}

            {/* Render Counters if available */}
            {comp.Properties?.Counters && comp.Properties.Counters.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Counters
                </Typography>
                {comp.Properties.Counters.map((counter, cIdx) => (
                  <Box key={cIdx} sx={{ display: "flex", mb: 1 }}>
                    <GenericInput
                      type="text"
                      name={`CounterValue-${idx}-${cIdx}`}
                      title="Value"
                      value={counter.Value}
                      error={error?.Items?.[idx]?.Properties?.Counters?.[cIdx]?.Value || ""}
                      onChange={(e) =>
                        handleCounterChange(idx, cIdx, "Value", getValue(e))
                      }
                      onBlur={() => onblur("CounterValue", cIdx)}
                      disabled={false}
                    />
                    <GenericInput
                      type="text"
                      name={`CounterLabel-${idx}-${cIdx}`}
                      title="Label"
                      value={counter.Label}
                      error={error?.Items?.[idx]?.Properties?.Counters?.[cIdx]?.Label || ""}
                      onChange={(e) =>
                        handleCounterChange(idx, cIdx, "Label", getValue(e))
                      }
                      onBlur={() => onblur("CounterLabel", cIdx)}
                      disabled={false}
                    />
                  </Box>
                ))}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddCounter(idx)}
                  sx={{ mb: 2 }}
                >
                  Add Counter
                </Button>
              </>
            )}

            {/* Render History Items if present */}
            {comp.Items && comp.Items.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  History
                </Typography>
                {comp.Items.map((historyItem, hIdx) => (
                  <Box
                    key={hIdx}
                    sx={{
                      border: "1px solid #ddd",
                      p: 1,
                      borderRadius: "4px",
                      mb: 1,
                    }}
                  >
                    <GenericInput
                      type="text"
                      name={`HistoryDate-${idx}-${hIdx}`}
                      title="Date"
                      value={historyItem.Date || ""}
                      error={error?.Items?.[idx]?.Items?.[hIdx]?.Date || ""}
                      onChange={(e) =>
                        handleHistoryChange(idx, hIdx, "Date", getValue(e))
                      }
                      onBlur={() => onblur("HistoryDate", hIdx)}
                      disabled={false}
                    />
                    <GenericInput
                      type="text"
                      name={`HistoryTitle-${idx}-${hIdx}`}
                      title="History Title"
                      value={historyItem.Title || ""}
                      error={error?.Items?.[idx]?.Items?.[hIdx]?.Title || ""}
                      onChange={(e) =>
                        handleHistoryChange(idx, hIdx, "Title", getValue(e))
                      }
                      onBlur={() => onblur("HistoryTitle", hIdx)}
                      disabled={false}
                    />
                    <GenericTextAreaInput
                      name={`HistoryDescription-${idx}-${hIdx}`}
                      title="History Description"
                      value={historyItem.Description || ""}
                      error={error?.Items?.[idx]?.Items?.[hIdx]?.Description || ""}
                      onChange={(e) =>
                        handleHistoryChange(idx, hIdx, "Description", getValue(e))
                      }
                      onBlur={() => onblur("HistoryDescription", hIdx)}
                      disabled={false}
                    />
                  </Box>
                ))}
              </>
            )}

            {/* Render Tabs if available */}
            {comp.Tabs && comp.Tabs.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Mission Tabs
                </Typography>
                {comp.Tabs.map((tab, tIdx) => (
                  <Box
                    key={tIdx}
                    sx={{
                      border: "1px solid #ddd",
                      p: 1,
                      borderRadius: "4px",
                      mb: 1,
                    }}
                  >
                    <GenericInput
                      type="text"
                      name={`TabTitle-${idx}-${tIdx}`}
                      title="Tab Title"
                      value={tab.Title}
                      error={error?.Items?.[idx]?.Tabs?.[tIdx]?.Title || ""}
                      onChange={(e) => handleTabChange(idx, tIdx, "Title", getValue(e))}
                      onBlur={() => onblur("TabTitle", tIdx)}
                      disabled={false}
                    />
                    <GenericTextAreaInput
                      name={`TabDescription-${idx}-${tIdx}`}
                      title="Tab Description"
                      value={tab.Description}
                      error={error?.Items?.[idx]?.Tabs?.[tIdx]?.Description || ""}
                      onChange={(e) => handleTabChange(idx, tIdx, "Description", getValue(e))}
                      onBlur={() => onblur("TabDescription", tIdx)}
                      disabled={false}
                    />
                  </Box>
                ))}
              </>
            )}

            {/* Render Partner Images if available */}
            {comp.Images && comp.Images.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Partners
                </Typography>
                <Grid container spacing={2}>
                  {comp.Images.map((img, pIdx) => (
                    <Grid item xs={12} sm={4} md={3} key={pIdx}>
                      <Box
                        sx={{
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          height: 100,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          backgroundColor: "#f9f9f9",
                          overflow: "hidden",
                        }}
                        onClick={() => openImagePicker(idx, "Images", pIdx)}
                      >
                        {img ? (
                          <img
                            src={img}
                            alt={`Partner ${pIdx + 1}`}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Select Image
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Button
                  variant="contained"
                  onClick={() => {
                    // Append a new empty image slot.
                    const updatedItems = [...companyItems];
                    const compItem = updatedItems[idx];
                    const images = compItem.Images || [];
                    updatedItems[idx] = { ...compItem, Images: [...images, ""] };
                    onChange({ ...item, Items: updatedItems }, "Items");
                  }}
                  sx={{ mt: 2 }}
                >
                  Add Partner Image
                </Button>
              </>
            )}
          </Box>
        ))}

        <Button
          variant="contained"
          onClick={() => {
            // Add a new company section (an empty object).
            const newSection: ICompanyItem = {};
            onChange({ ...item, Items: [...companyItems, newSection] }, "Items");
          }}
          startIcon={<AddIcon />}
        >
          Add New Section
        </Button>

        {/* Filters Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Filters
        </Typography>
        {filters.map((filter, idx) => (
          <Box
            key={idx}
            sx={{ border: "1px solid #ddd", p: 2, borderRadius: "4px", mb: 2 }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <GenericInput
                  type="text"
                  name={`FilterTitle-${idx}`}
                  title="Filter Title"
                  value={filter.Title}
                  error={error?.Filters?.[idx]?.Title || ""}
                  onChange={(e) => handleFilterChange(idx, "Title", getValue(e))}
                  onBlur={() => onblur("FilterTitle", idx)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <GenericInput
                  type="text"
                  name={`FilterLink-${idx}`}
                  title="Filter Link"
                  value={filter.Link}
                  error={error?.Filters?.[idx]?.Link || ""}
                  onChange={(e) => handleFilterChange(idx, "Link", getValue(e))}
                  onBlur={() => onblur("FilterLink", idx)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <IconButton
                  color="error"
                  onClick={() => handleRemoveFilter(idx)}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddFilter}>
          Add Filter
        </Button>
      </CardContent>
      {imagePickerOpen && currentImageTarget && (
        <ImagePicker
          open={imagePickerOpen}
          multiSelect={false}
          onClose={() => setImagePickerOpen(false)}
          onSelect={(selected) => {
            handleImageSelect(selected);
          }}
        />
      )}
    </Card>
  );
};

export default CompanyDetailsItem;
